import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Tabs, tabPanelId } from '@/components/ui/Tabs';
import { SCHOOL_STATUS_LABEL, SCHOOL_STATUS_TONE } from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { institutesApi } from '../api/institutes.api';
import {
  AcademicStep,
  ContactStep,
  IdentityStep,
  PeopleStep,
} from '../components/InstituteWizardSteps';
import {
  STEP_FIELDS,
  instituteSchema,
  stepPayload,
  type InstituteFormValues,
} from '../schemas/institute.schema';
import { applyFieldErrors, toMobileDigits } from '@/lib/forms';
import { LoadingBlock } from '@/components/ui/Loader';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

const TABS = [
  { value: 'identity', label: 'Identity & recognition' },
  { value: 'academic', label: 'Academic profile' },
  { value: 'contact', label: 'Address & contact' },
  { value: 'people', label: 'People' },
] as const;

type TabKey = (typeof TABS)[number]['value'];

/**
 * Editing an existing institute.
 *
 * Reuses the wizard's step components rather than restating sixty fields
 * (CLAUDE.md §3) — as tabs instead of steps, because someone correcting a phone
 * number should not have to walk through four screens to reach it.
 *
 * An institute's operating settings — the institute day, grading, numbering series —
 * are edited on their own screen, not here: fixing an address and changing how
 * admission numbers are issued are different decisions.
 */
export function EditInstitutePage() {
  const { uuid = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const detailPath = `/platform/institutes/${uuid}`;

  /**
   * Where "back" and "cancel" go — wherever this screen was opened from.
   *
   * It used to always return to the record, so editing from the customer list
   * dropped the reader on a screen they had never been on and made getting back
   * to the list two more clicks. Whoever links here says where they came from;
   * the record stays the fallback, for a link pasted into chat or a page
   * reloaded, which carry no history.
   *
   * Only paths inside this section are honoured. The value arrives as router
   * state rather than in the URL, so it cannot be set by a stranger's link, but
   * a "back" button is not a general-purpose redirector either way.
   */
  const cameFrom = (location.state as { from?: string } | null)?.from;
  const returnTo = cameFrom?.startsWith('/platform/institutes') ? cameFrom : detailPath;
  const returnLabel = returnTo.startsWith(detailPath) ? 'Back to institute' : 'Back to institutes';

  const [tab, setTab] = useState<TabKey>('identity');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  /** Whether any tab was written this visit, so the record can say "updated". */
  const [hasSaved, setHasSaved] = useState(false);

  const { data: institute, isLoading, error } = useApiQuery(() => institutesApi.get(uuid), [uuid]);

  const form = useForm<InstituteFormValues>({
    // `raw`, as on the create wizard: `stepPayload` parses once itself, and a
    // second parse of zod's output refused a blank due date (already `null`)
    // and turned a cleared pin into 0, 0 — every save of an institute without
    // a due date failed with "Something went wrong".
    resolver: zodResolver(instituteSchema, undefined, { raw: true }),
    // The same fields as the create wizard, checked the same way (see there).
    mode: 'all',
  });
  useLiveFormErrors(form);

  const {
    register,
    control,
    watch,
    reset,
    setValue,
    setError,
    trigger,
    getValues,
    getFieldState,
    resetField,
    formState: { errors, isDirty },
  } = form;

  // Fill the form once the institute arrives.
  useEffect(() => {
    if (!institute) return;

    reset({
      code: institute.code,
      subdomain: institute.subdomain ?? '',
      legalName: institute.legalName,
      displayName: institute.displayName,
      shortName: institute.shortName ?? '',
      motto: institute.motto ?? '',
      udiseCode: institute.udiseCode ?? '',
      institutionType: institute.institutionType,
      // Null for a college; the form field is optional rather than nullable.
      board: institute.board ?? undefined,
      stateBoardName: institute.stateBoardName ?? '',
      affiliationNumber: institute.affiliationNumber ?? '',
      affiliationValidTill: institute.affiliationValidTill?.slice(0, 10) ?? '',
      recognitionNumber: institute.recognitionNumber ?? '',
      establishedYear: institute.establishedYear ?? '',
      managementType: institute.managementType,
      trustName: institute.trustName ?? '',
      trustRegistrationNo: institute.trustRegistrationNo ?? '',
      panNumber: institute.panNumber ?? '',
      gstin: institute.gstin ?? '',
      // Left blank on purpose: only its last four come back, and a blank field
      // keeps what is saved. The hint under the field says one is on file.
      aadhaarNumber: '',

      schoolLevel: institute.schoolLevel ?? undefined,
      collegeLevel: institute.collegeLevel ?? undefined,
      lowestClass: institute.lowestClass as InstituteFormValues['lowestClass'],
      highestClass: institute.highestClass as InstituteFormValues['highestClass'],
      genderType: institute.genderType,
      residentialType: institute.residentialType,
      mediumOfInstruction: institute.mediumOfInstruction,
      streams: institute.streams ?? [],

      addressLine1: institute.addressLine1,
      addressLine2: institute.addressLine2 ?? '',
      locality: institute.locality ?? '',
      city: institute.city,
      district: institute.district,
      state: institute.state as InstituteFormValues['state'],
      pincode: institute.pincode,
      latitude: institute.latitude ?? '',
      longitude: institute.longitude ?? '',
      // Saved as +91XXXXXXXXXX; the field draws the +91 itself and holds the ten digits.
      phone: toMobileDigits(institute.phone ?? ''),
      alternatePhone: toMobileDigits(institute.alternatePhone ?? ''),
      whatsappNumber: toMobileDigits(institute.whatsappNumber ?? ''),
      email: institute.email,
      website: institute.website ?? '',

      principalName: institute.principalName,
      principalPhone: toMobileDigits(institute.principalPhone ?? ''),
      principalEmail: institute.principalEmail ?? '',
      adminContactName: institute.adminContactName,
      adminContactDesignation: institute.adminContactDesignation ?? '',
      adminContactPhone: toMobileDigits(institute.adminContactPhone ?? ''),
      adminContactEmail: institute.adminContactEmail,

      /*
        Null means no fee has been agreed; the field shows empty rather than a
        zero nobody typed. The amount arrives as a string — as every amount
        does — and becomes a number only here, to sit in a number input. It goes
        back to the server through the same schema the create form uses.
      */
      subscriptionAmount:
        institute.subscriptionAmount === null ? '' : Number(institute.subscriptionAmount),
      paymentStatus: institute.paymentStatus,
      subscriptionDueDate: institute.subscriptionDueDate?.slice(0, 10) ?? '',
    });
  }, [institute, reset]);

  const tabIndex = TABS.findIndex((item) => item.value === tab);
  const isLastTab = tabIndex === TABS.length - 1;

  /**
   * Saves the tab on show, then moves to the next one.
   *
   * Only this tab's fields are checked and sent. Checking the whole record
   * meant a blank field on People — common on institutes created before it was
   * required — stopped a save made from Identity and threw the reader to the
   * last tab. Each tab is now finished in order, as in the create wizard, and
   * the server's update takes any subset of fields. A tab with nothing changed
   * is checked and passed through without a request.
   */
  async function saveTab() {
    setFormError(null);
    const fields = STEP_FIELDS[tabIndex] as readonly (keyof InstituteFormValues)[];

    const isValid = await trigger([...fields], { shouldFocus: true });
    if (!isValid) return;

    setIsSaving(true);
    try {
      const changed = fields.some((field) => getFieldState(field).isDirty);
      if (changed) {
        await institutesApi.update(uuid, stepPayload(tabIndex, getValues()));
        setHasSaved(true);
        // What was just written is the new baseline, so "Unsaved changes"
        // speaks only of the tabs still to come.
        for (const field of fields) resetField(field, { defaultValue: getValues(field) });
      }

      if (isLastTab) {
        // Finishing lands on the record, wherever the edit was opened from:
        // the confirmation belongs beside the values that changed.
        navigate(detailPath, {
          replace: true,
          state: hasSaved || changed ? { justUpdated: true } : undefined,
        });
        return;
      }

      if (changed) toast.success(`${TABS[tabIndex].label} saved`);
      setTab(TABS[tabIndex + 1].value);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  if (error) {
    return (
      <Alert tone="critical" title="Could not load this institute">
        {error.message}
      </Alert>
    );
  }

  if (isLoading || !institute) {
    return (
      <>
        <DeckPageHeader
          eyebrow="Editing"
          title="Institute"
          description="Fetching this customer's record."
          backTo={{ to: returnTo, label: returnLabel }}
        />
        <div className="card p-5">
          <LoadingBlock label="Loading this institute" />
        </div>
      </>
    );
  }

  const stepProps = {
    register,
    errors,
    control,
    watch,
    setValue,
    isEdit: true,
    savedAadhaarLast4: institute?.aadhaarLast4 ?? null,
  };

  return (
    <>
      {/* Which record you are editing, said on the banner: the code and the
          account's state are what tell two institutes of similar name apart,
          and this screen writes to whichever one is named up here. */}
      <DeckPageHeader
        eyebrow="Editing"
        title={institute.displayName}
        description="The institute code is assigned by the system and cannot be changed — it appears on receipts."
        backTo={{ to: returnTo, label: returnLabel }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-pill border border-line bg-surface px-3 py-1 font-mono text-2xs text-ink-2">
            {institute.code}
          </span>
          <Badge tone={SCHOOL_STATUS_TONE[institute.status]}>
            {SCHOOL_STATUS_LABEL[institute.status]}
          </Badge>
          {/* Only while there is something to save — a permanent "unsaved"
              hint is a hint nobody reads by the third screen. */}
          {isDirty && (
            <span className="rounded-pill border border-warn bg-warn-bg px-3 py-1 text-2xs font-[650] text-warn">
              Unsaved changes
            </span>
          )}
        </div>
      </DeckPageHeader>

      {formError && (
        <Alert tone="critical" title="Could not save">
          {formError}
        </Alert>
      )}

      {/* The product's own tab strip rather than a hand-rolled row of buttons:
          this screen had four `role="tab"` buttons with no roving focus, no
          `aria-controls` and no arrow-key movement, which is not a tablist to
          anyone listening to it (CLAUDE.md §3). */}
      <Tabs
        options={TABS}
        value={tab}
        onChange={setTab}
        label="Which part of the record to edit"
      />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void saveTab();
        }}
        noValidate className="flex flex-col gap-6">
        <div className="card p-5 lg:p-6">
          {/* Hidden rather than unmounted: unmounting a tab would drop the
              values typed in it before save. */}
          {TABS.map((item) => (
            <div
              key={item.value}
              id={tabPanelId(item.value)}
              role="tabpanel"
              aria-labelledby={`${item.value}-tab`}
              className={tab === item.value ? '' : 'hidden'}
            >
              {item.value === 'identity' && <IdentityStep {...stepProps} />}
              {item.value === 'academic' && <AcademicStep {...stepProps} />}
              {item.value === 'contact' && <ContactStep {...stepProps} />}
              {item.value === 'people' && <PeopleStep {...stepProps} />}
            </div>
          ))}
        </div>

        {/* Pinned, like the wizard's: these forms are long, and "Save changes"
            should not be somewhere you have to go and find. */}
        <div className="sticky bottom-4 z-10 flex items-center justify-between gap-3 rounded-card border border-line bg-surface p-3 shadow-card">
          {/* Abandoning the edit puts you back where you started, same as the
              header link — not on a screen you never asked for. */}
          <Button variant="ghost" onClick={() => navigate(returnTo)}>
            Cancel
          </Button>
          {/* Always offered: each tab is saved on its own terms, and the button
              must never wait on a change somewhere else in the record. */}
          <Button
            type="submit"
            size="lg"
            isLoading={isSaving}
            leftIcon={<Save className="h-4 w-4" />}
          >
            {isSaving ? 'Saving…' : isLastTab ? 'Save changes' : 'Save and continue'}
          </Button>
        </div>
      </form>
    </>
  );
}
